import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Form, FormProps, Modal, Select } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap';
import { Services } from '../../api/Services';
import { Option } from '../../models/generics';
import { FrameworkOption } from '../../utils';
import { useToasts } from '../../contexts/toasts';
import { Frameworks } from '../../api/Frameworks';

export interface PaletteFilters {
  area?: string;
  framework?: string;
  status?: string;
  page?: number;
  search?: string;
}

type Props = {
  show?: boolean;
  onClose?: () => void;
  onSubmit?: (filters: any) => void;
  selectedGroup?: string;
  filters?: PaletteFilters;
}

const formProps: FormProps = {
  layout: 'horizontal',
  colon: false,
  labelAlign: 'left',
  labelCol: {
    md: 8
  },
}

export default function PaletteFiltersModal({ show, onClose, selectedGroup = "services", filters, onSubmit }: Props) {
  const [form] = Form.useForm();
  const [frameworkOptions, setFrameworkOptions] = useState<FrameworkOption[]>();
  const [areasOptions, setAreasOptions] = useState<Option[]>();

  const { showToast } = useToasts();

  useEffect(() => form.setFieldsValue(filters), [form, filters]);

  const initAreas = useCallback(() => {
    Services.getAreas()
      .then(
        ({ data: areas }) => {
          setAreasOptions(
            areas.map(area => ({
              label: area.split("_").join(" "),
              value: area,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('areas-error', message, `Areas Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const initFrameworks = useCallback(() => {
    Frameworks.getFrameworks()
      .then(
        ({ data: frameworks }) => {
          setFrameworkOptions(
            frameworks.map(f => ({
              label: f.name,
              value: f.id,
              image: f.imageUrl,
              trailing: f.version,
            }))
          );
        },
        (err) => {
          showToast('frameworks-error', 'Could not get frameworks', 'Frameworks Error', 'danger');
          setFrameworkOptions([]);
        }
      )
  }, [showToast]);

  useEffect(() => {
    if (show && selectedGroup === "services") {
      if (!frameworkOptions) initFrameworks();
      if (!areasOptions) initAreas();
    }
  }, [show, selectedGroup, initFrameworks, initAreas, areasOptions, frameworkOptions]);

  const handleSubmit = useCallback((values: PaletteFilters) => onSubmit?.(values), [onSubmit]);

  const renderServiceFilters = useCallback(() => {
    return (
      <>
        <Form.Item name="area" label="Area">
          <Select
            allowClear
            loading={!areasOptions}
            placeholder="Select area"
            options={areasOptions}
            className="w-100"
          />
        </Form.Item>
        <Form.Item name="framework" label="Framework">
          <Select
            loading={!frameworkOptions}
            placeholder="Select framework"
            className="w-100"
            allowClear
          >
            {frameworkOptions?.map((option, index) => (
              <Select.Option key={index} value={option.value} label={option.label}>
                <div className="d-flex align-items-center">
                  <div className="framework-image" style={{ backgroundImage: `url(${option.image})` }}></div>
                  <span className="ms-2">{option.label}</span>
                  <small className="text-muted ms-3">{option.trailing}</small>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </>
    );
  }, [areasOptions, frameworkOptions]);

  return (
    <Modal
      open={show}
      onCancel={onClose}
      onClose={onClose}
      title={<h6 className="text-capitalize mb-3">{selectedGroup} filters</h6>}
      footer={null}
    >
      <Form {...formProps} form={form} onFinish={handleSubmit}>
        {selectedGroup === 'services' && renderServiceFilters()}

        <Flex justify="space-between" align="center">
          <Button variant="light" size="sm" className="border" onClick={onClose}>Close</Button>
          <Button variant="success" size="sm" type="submit">
            <FontAwesomeIcon icon={solid('search')} />
            <span className="ms-2">Search</span>
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}